import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Bar } from "@/pages/_shared";

export function ScoreSimulator({ baseScore, scale = 850, levers = [], title = "Simulate your score", note }) {
  const [active, setActive] = useState([]);
  const gain = levers.filter((l) => active.includes(l.id)).reduce((s, l) => s + l.points, 0);
  const projected = Math.min(scale, baseScore + gain);
  const pct = (projected / scale) * 100;
  const toggle = (id) => setActive((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  return (
    <Card className="p-5" data-testid="score-simulator">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1a5fd0]/10"><Sparkles size={17} className="text-[#1a5fd0]" /></div>
        <div>
          <div className="font-head text-[15px] font-bold text-slate-900">{title}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Interactive · SANDBOX estimate</div>
        </div>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-2">
          {levers.map((l) => {
            const on = active.includes(l.id);
            return (
              <button key={l.id} onClick={() => toggle(l.id)} data-testid="simulator-lever"
                className={`flex w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors ${on ? "border-[#1a5fd0] bg-[#1a5fd0]/5" : "border-slate-200 hover:border-slate-300"}`}>
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[11px] ${on ? "border-[#1a5fd0] bg-[#1a5fd0] text-white" : "border-slate-300"}`}>{on ? "✓" : ""}</span>
                <span className="flex-1">
                  <span className="block text-[13px] font-semibold text-slate-800">{l.label}</span>
                  {l.detail && <span className="mt-0.5 block text-[11px] text-slate-400">{l.detail}</span>}
                </span>
                <span className="shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 font-mono text-[11px] font-bold text-emerald-600">+{l.points}</span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-col items-center justify-center rounded-2xl bg-slate-50 p-6 text-center">
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Projected score</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-head text-5xl font-extrabold tabular-nums text-[#1a5fd0]" data-testid="simulator-projected">{projected}</span>
            <span className="font-mono text-xs text-slate-400">/ {scale}</span>
          </div>
          {gain > 0
            ? <span className="mt-1 rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-[12px] font-bold text-emerald-600">+{gain} pts projected</span>
            : <span className="mt-1 text-[12px] text-slate-400">Select actions to project impact</span>}
          <div className="mt-4 w-full"><Bar pct={pct} tone="#1a5fd0" /></div>
          <div className="mt-1 flex w-full justify-between font-mono text-[10px] text-slate-400"><span>Now {baseScore}</span><span>{scale}</span></div>
        </div>
      </div>
      <div className="mt-3 text-[10px] text-slate-400">{note || "Estimated impact on your MMT Funding Readiness Score. Modeled projection only — not a guarantee of approval, score change, or amount."}</div>
    </Card>
  );
}
